import { useNavigate, useLocation } from "react-router-dom";
import { useContext, useState, useEffect } from "react";
import { Typography, Divider, Button, Box } from "@mui/material";
import { AuthContext } from "src/utils/AuthContext.js";
import { StarsSelect } from "src/components/blocks/StarsSelect.js";
import ShareDialog from "src/components/blocks/dialogs/ShareDialog.js";
import PersonalizeTags from "src/components/blocks/tags/PersonalizeTags.js";
import BenefitsCarousel from "src/components/upgrades/BenefitsCarousel.js";
import InstructionsLayout from "../InstructionsLayout.js";
import { getPageRoute } from "src/services";
import confetti from "canvas-confetti";
import { customEvent } from "src/utils/gtag.js";
import useGetCalendarFromParams from "src/utils/useGetCalendarFromParams.js";

const SuccessfulCalendarSubscription = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [stars, setStars] = useState(0);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const { calendar } = useGetCalendarFromParams();
  const isReminder = location.state?.isReminder ?? false;

  console.log(isReminder);

  const boomTitle = (
    <span style={{ fontWeight: 400 }}>
      <span style={{ fontWeight: 600 }}>Boom!</span> <br /> You're all set.
    </span>
  );

  const description = isReminder ? (
    `Follow the instructions in the reminder to subscribe to "${calendar?.name}" on your desktop, then sync it with the Google Calendar app on your phone.`
  ) : (
    <>
      Your calendar will now <b>automatically update</b> whenever `
      {calendar?.name}` events are added, changed, or cancelled.
    </>
  );

  useEffect(() => {
    const count = 200;
    const defaults = {
      origin: { y: 0.7 },
      zIndex: 1500,
      colors: ["#000000", "#FFFFFF"], // Black and white colors
    };

    function fire(particleRatio, opts) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
      });
    }

    // First burst - black and white mix
    fire(0.4, {
      spread: 100,
      startVelocity: 55,
      scalar: 0.8,
      colors: ["#000000", "#FFFFFF"],
    });

    // Second burst - mostly white
    fire(0.3, {
      spread: 60,
      startVelocity: 45,
      colors: ["#FFFFFF"],
    });

    // Third burst - mostly black
    fire(0.3, {
      spread: 80,
      startVelocity: 35,
      colors: ["#000000"],
    });

    return () => confetti.reset();
  }, []);

  useEffect(() => {
    customEvent({
      name: "landed_on_subscription_success",
      category: "CalendarSubscription",
      label: "Landed on Calendar Subscription Success Page",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        calendar_handle: calendar?.handle,
      },
    });
  }, [stars]);

  return (
    <InstructionsLayout
      nav="home"
      sx={{ textAlign: "left", maxWidth: "466px" }}
    >
      <Typography variant="h3" sx={{ mb: 2 }}>
        {boomTitle}
      </Typography>
      <Typography variant="body1" sx={{ color: "text.primary", mb: 4 }}>
        {description}
      </Typography>
      <Button
        variant="contained"
        fullWidth
        onClick={() => setShareDialogOpen(true)}
        sx={{ mb: 2 }}
      >
        Share
      </Button>
      <ShareDialog
        calendar={calendar}
        handle={calendar?.handle}
        open={shareDialogOpen}
        setOpen={setShareDialogOpen}
        as="Hidden"
      />
      <Button
        variant="outlined"
        fullWidth
        href={getPageRoute(
          "home",
          "HOME",
          {},
          { tab: user?.tags?.length > 0 ? "foryou" : "discover" }
        )}
      >
        Explore Library
      </Button>
      <Divider sx={{ width: "100%", my: 4 }} />
      <StarsSelect stars={stars} setStars={setStars} calendar={calendar} />
      <Divider sx={{ width: "100%", mt: 4 }} />

      {/* Benefits Carousel */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 4 }}>
        <BenefitsCarousel
          variant="dark"
          showDownloadSample={false}
          customCTA="GET 7 DAYS FREE"
          onUpgradeClick={() => {
            navigate(getPageRoute("upgrade", "UPGRADE_PLUS"), {
              state: { source: "subscription_success_page" },
            });
            customEvent({
              name: "user_click_successful_subscription_upgrade",
              category: "Upgrade",
              label: "Benefits Carousel Upgrade Button (Calendar Success)",
            });
          }}
          sx={{ maxWidth: "400px" }}
        />
      </Box>
      <Divider sx={{ width: "100%", mt: 4 }} />

      {/* Tags Selection Section */}
      <Box sx={{ placeItems: "flex-start", textAlign: "left", mt: 4, mb: 8 }}>
        <Typography variant={"title"}>Finish what you started</Typography>
        <Typography
          variant={"subtitle2"}
          sx={{ mt: 2.5, mb: 5 }}
          color="text.secondary"
        >
          Choose tags that match your interests so we can help you find more of
          what you like.
        </Typography>

        <PersonalizeTags
          onTagChange={(action, tag) => {
            customEvent({
              name: `${action}_subscription_success_tag`,
              category: "SubscriptionSuccess",
              label: `User ${action} subscription success tag`,
              data: {
                user_id: user?._id ?? "guest",
                user_email: user?.email ?? "-",
                user_handle: user?.handle ?? "no_handle",
                calendar_handle: calendar?.handle,
                tag,
              },
            });
          }}
        />
      </Box>

      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "20px",
          backgroundColor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "center",
          zIndex: 1000,
        }}
      >
        <Button
          variant="contained"
          size="grown"
          sx={{ width: "100%", maxWidth: "400px" }}
          onClick={() => {
            navigate(
              getPageRoute(
                "home",
                "HOME",
                {},
                { tab: user?.tags?.length > 0 ? "foryou" : "discover" }
              )
            );
          }}
        >
          EXPLORE CALENDARS
        </Button>
      </Box>
    </InstructionsLayout>
  );
};

export default SuccessfulCalendarSubscription;
